import { AnnotationResultsMessage, Response, SyncMessage, CacheDumpRequestMessage, AnnotationResultStateMessage, AnnotationConfigUpdateMessage, WorkflowGraphInitMessage } from '@augloop/session-protocol';
import { WorkflowRegistrationMessage } from '@augloop/session-protocol-internal';
import { SchemaObject, UpdateAnnotationMetaDataOperation, SignalOperation, AddOperation, Operation, UpdateOperation, DeleteOperation, DeltaUpdateOperation } from '@augloop/types-core';
import { AuthTokenType as ClientAuthTokenType } from '@augloop/schemas';
import { AnnotationActivationTracker } from './annotation-activation-tracker';
import { BatchManager } from './batch-manager';
import { estimateSize } from './estimate-size';
import { EmptySessionManager } from './websocket/empty-session-manager';
import { AuthTokenType, WorkflowKind } from '@augloop/interfaces';
import { isServerOp, Utils } from './utils';
import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { getContextTypes } from '@augloop/workflow-requested-contexts';
import { makeItemPathKey, shallowCloneItemWithPath } from '@augloop/session-cache';
import { DynamicWorkflowDefinitionManager, ContextIdManager, WorkflowItemStorage, getWorkflowDefinitionBase, WorkflowGraph, supportedOperationSet } from '@augloop/mastermind-shared';
import { DeltaGenerator } from '@augloop/delta-generator';
export const graphInitDelayMs = 500;
const getClientAuthTokenType = (tokenType) => {
    // Right now only have a scenario that involves Substrate, we might have more in the future
    if (tokenType === AuthTokenType.Substrate) {
        return ClientAuthTokenType.Substrate;
    }
    return undefined;
};
export class SessionProxy {
    constructor(config) {
        var _a;
        this.annotationActivationTrackers = new Map();
        this.annotationCallbacks = new Map();
        this.annotationResultStates = new Map();
        this.tokensByAnnotationType = new Map();
        this.registeredContextTypes = new Set();
        // TODO #5326116: This map acts as a session cache needed for contexts. Remove it in favor of regular session cache once it's available.
        this.availableContexts = new Map();
        this.nextSyncSequenceId = 1;
        this.allowSeed = true;
        this.allowGroupSeed = true;
        this.seedGroupSize = 0;
        this.hasSessionConnected = false;
        this.isSessionClosed = false;
        this.isSessionConnected = false;
        this.workflowGraph = new WorkflowGraph();
        this.executionTrackersByWorkflowName = new Map();
        this.workflowDefinitionManager = new DynamicWorkflowDefinitionManager();
        this.contextIdManager = new ContextIdManager("C" /* JsClient */, this.workflowGraph);
        this.workflowItemStorage = new WorkflowItemStorage(this.workflowDefinitionManager);
        this.graphInitMessageTimer = false;
        this.hostCallbacks = config.hostCallbacks;
        this.sessionManager = config.sessionManager;
        this.operationBatchConfig = config.batchOptions ? this.getOperationBatchConfig(config.batchOptions) : undefined;
        this.extensionConfigs = config.extensionConfigs;
        this.clientMetadata = config.clientMetadata;
        this.localWorkflowManager = config.localWorkflowManager;
        this.annotationResultsProcessor = config.annotationResultsProcessor;
        if (config.workflowRuntimeFactory) {
            this.workflowRuntime = config.workflowRuntimeFactory(this.sessionManager);
        }
        this.localRegisteredWorkflows = config.localRegisteredWorkflows || [];
        this.skipGraphInitOnWorkflowRegistration = config.skipGraphInitOnWorkflowRegistration;
        this.sessionManager.on('sessionClose', this.onSessionClose.bind(this));
        this.sessionManager.on('reconnect', this.onReconnect.bind(this));
        this.sessionManager.on('connect', this.onConnect.bind(this));
        this.sessionManager.on('disconnect', this.onDisconnect.bind(this));
        this.sessionManager.on('resetNextSyncSequenceId', this.resetNextSyncSequenceId.bind(this));
        this.sessionManager.onMessage(AnnotationResultsMessage.getTypeName(), this.onAnnotationResultsFromServer.bind(this));
        this.sessionManager.onMessage(AnnotationResultStateMessage.getTypeName(), this.onAnnotationResultStateMessage.bind(this));
        if (this.clientMetadata && ((_a = this.clientMetadata.flights) === null || _a === void 0 ? void 0 : _a.indexOf('Microsoft.Office.WordOnline.AugloopDeltas')) !== -1) {
            this.enableDeltaGenerator();
        }
    }
    initialize() {
        this.localWorkflowManager.addSession(this);
        for (const workflow of this.localRegisteredWorkflows) {
            this.localWorkflowManager.registerLocalWorkflow(workflow, this);
        }
        return this.sessionManager.init(this.extensionConfigs, this.hostCallbacks.onInitSession ? this.hostCallbacks.onInitSession : undefined)
            .then(() => {
            if (this.workflowRuntime) {
                // TODO.Phase3 (5877784): Set token callback
                return this;
            }
            this.localWorkflowManager.setTokenCallback(this.getAuthToken.bind(this));
            return this;
        });
    }
    get isConnected() {
        return this.isSessionConnected;
    }
    get hasConnected() {
        return this.hasSessionConnected;
    }
    get isClosed() {
        return this.isSessionClosed;
    }
    getContextIdManager() {
        return this.contextIdManager;
    }
    getWorkflowItemStorage() {
        return this.workflowItemStorage;
    }
    getWorkflowDefinition(workflow, contextId) {
        return this.workflowDefinitionManager.getWorkflowDefinition(workflow, contextId);
    }
    registerLocalWorkflows(workflows) {
        for (const workflow of workflows) {
            if (this.workflowRuntime) {
                this.workflowRuntime.registerWorkflow(workflow);
                this.sessionManager.sendMessage(new WorkflowRegistrationMessage({
                    workflowDefinition: workflow
                }));
                return;
            }
            this.localWorkflowManager.registerLocalWorkflow(workflow, this);
        }
        // Try send WorkflowGraphInitMessage, since we just added a new workflows
        this.trySendWorkflowGraphInitMessage();
    }
    registerLocalWorkflow(workflow) {
        this.registerLocalWorkflows([workflow]);
        // Adding this log to track all the Apps/Platforms that still uses session.registerLocalWorkflow over session.registerLocalWorkflows
        const logOp = new LogOperation({ operationName: 'SessionRegisterLocalWorkflow', resourceId: workflow.id, success: true }).start();
        Log.info(0x1f2e18c4 /* tag_5l79e */, LogCategory.CoreDefault, logOp.stop());
    }
    activateAnnotation(annotationType, options) {
        var _a;
        const logOp = new LogOperation({ operationName: 'ActivateAnnotation', resourceId: annotationType, success: true })
            .setClientMetadata(this.clientMetadata)
            .start();
        const tracker = new AnnotationActivationTracker(this.getSessionManager.bind(this), annotationType, options);
        if (options && options.callback) {
            let map = this.annotationCallbacks.get(annotationType);
            if (!map) {
                map = new Map();
                this.annotationCallbacks.set(annotationType, map);
            }
            map.set(tracker.token, options.callback);
        }
        const tokens = (_a = this.tokensByAnnotationType.get(annotationType)) !== null && _a !== void 0 ? _a : [];
        if (tokens.indexOf(tracker.token) === -1) {
            tokens.push(tracker.token);
        }
        this.tokensByAnnotationType.set(annotationType, tokens);
        this.annotationActivationTrackers.set(tracker.token, tracker);
        tracker.activate(false /*ignoreExistingAnnotations*/, false /*sendOnlyIfConnected*/);
        return tracker.asPromise()
            .then(results => {
            logOp.resultSignature = 'Ok';
            logOp.resultDescription = `Activated annotation ${annotationType} with token ${tracker.token} `;
            Log.info(0x1f10c54f /* tag_5emvp */, LogCategory.CoreDefault, logOp.stop());
            return results;
        })
            // TODO 6125121: Fix annotation activation timeout message error
            .catch(error => {
            logOp.success = false;
            logOp.resultDescription = `error on activate annotation type ${annotationType}: ${error}`;
            Log.error(0x1f48025f /* tag_5saj5 */, LogCategory.CoreDefault, logOp.stop());
        });
    }
    updateAnnotationConfig(token, config) {
        const msg = new AnnotationConfigUpdateMessage({ token, config });
        this.getSessionManager().sendMessage(msg);
        const tracker = this.annotationActivationTrackers.get(token);
        if (tracker && tracker.options) {
            // TODO: Expose annotation activation config to local workflows.
            // This is where the original config was stored, but there is no way to reach this from a local workflow yet.
            tracker.options.config = config;
        }
    }
    releaseAnnotation(token) {
        const tracker = this.annotationActivationTrackers.get(token);
        if (tracker) {
            this.annotationActivationTrackers.delete(token);
            const map = this.annotationCallbacks.get(tracker.annotationType);
            if (map) {
                map.delete(token);
                if (map.size == 0) {
                    this.annotationCallbacks.delete(tracker.annotationType);
                }
            }
            return tracker.release();
        }
        return Promise.resolve(false);
    }
    setAnnotationState(parentPath, id, value) {
        const M_ = { state: value };
        this.submitOperation(new UpdateAnnotationMetaDataOperation({ parentPath, items: [{ id: id }], M_ }));
    }
    setAnnotationMetadata(parentPath, id, value) {
        this.submitOperation(new UpdateAnnotationMetaDataOperation({ parentPath, items: [{ id: id }], M_: value }));
    }
    submitOperation(operation, cv) {
        this.submitOperations([operation], cv);
    }
    submitOperations(operations, cv) {
        this.contextIdManager.applyContextIdOnOperations(operations);
        this.increaseWorkflowsItemsToProcess(operations);
        if (this.workflowRuntime) {
            // TODO.Phase3 (6005906): Filter input types using GraphInit
            this.submitOperationsToSession(operations, cv);
            return;
        }
        const serverOps = operations.filter(op => isServerOp(op, this.serverInputTypes));
        // Delta component is only instantiated if the client app/platform/flight is allowed combination
        const updatedServerOps = [];
        if (this.deltaGenerator) {
            for (const op of serverOps) {
                if (UpdateOperation.typeGuard(op)) {
                    updatedServerOps.push(...this.createDeltasFromUpdateOperation(op));
                }
                else {
                    const opAs = op;
                    for (const item of opAs.items) {
                        if (AddOperation.typeGuard(op)) {
                            this.deltaGenerator.addItemToLocalMap(item, opAs.parentPath);
                        }
                        else if (DeleteOperation.typeGuard(op)) {
                            this.deltaGenerator.deleteItemFromLocalMap(item, opAs.parentPath);
                        }
                    }
                    updatedServerOps.push(op);
                }
            }
        }
        this.submitOperationsToSession(updatedServerOps.length ? updatedServerOps : serverOps, cv);
        this.localWorkflowManager.runLocalWorkflows(operations, this, true);
    }
    submitSeedOperations(operations, cv) {
        if (!this.allowSeed) {
            throw new Error('Cannot submit seed operations more than once per session');
        }
        this.allowSeed = false;
        this.allowGroupSeed = false;
        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, true, cv);
        }
        else {
            this.getSessionManager()
                .sendMessage(new SyncMessage({
                cv: cv,
                seq: 0,
                ops: operations
            }));
        }
    }
    submitSeedGroupOperations(operations, groupComplete, cv) {
        if (!this.allowGroupSeed) {
            throw new Error('Seed operations are not allowed for this session');
        }
        if (groupComplete) {
            this.allowGroupSeed = false;
        }
        this.allowSeed = false;
        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, !!groupComplete, cv);
        }
        else {
            this.seedGroupSize++;
            this.getSessionManager()
                .sendMessage(new SyncMessage({
                cv: cv,
                seq: 0,
                ops: operations,
                groupId: 'Seed',
                groupSize: groupComplete ? this.seedGroupSize : undefined,
                groupComplete: groupComplete ? groupComplete : undefined
            }));
        }
    }
    submitCustomMessage(message) {
        return new Promise((resolve, reject) => {
            this.getSessionManager()
                .sendMessage(message, (err, response) => {
                if (err) {
                    reject(new Error(err.error));
                }
                else {
                    resolve(response);
                }
            });
        });
    }
    requestCacheDump(cacheRequestParameters) {
        if (cacheRequestParameters) {
            throw new Error('NYI');
        }
        else {
            const toReturn = new Promise((resolve, reject) => {
                this.getSessionManager()
                    .sendMessage(new CacheDumpRequestMessage(), ((err, response) => {
                    if (!err) {
                        resolve(response);
                    }
                    else {
                        reject(err);
                    }
                }));
            });
            return toReturn;
        }
    }
    forceReconnect(extensionConfigs) {
        this.extensionConfigs = extensionConfigs;
        return this.getSessionManager().forceReconnect(extensionConfigs);
    }
    close() {
        this.isSessionClosed = true;
        this.getSessionManager().closeSession();
        if (this.workflowRuntime) {
            this.workflowRuntime.close();
        }
        this.localWorkflowManager.closeSession(this);
    }
    getClientMetadata() {
        return this.clientMetadata;
    }
    setSessionCloseCallback(callback) {
        this.sessionCloseCallback = callback;
    }
    setConnectCallback(callback) {
        this.connectCallback = callback;
    }
    setDisconnectCallback(callback) {
        this.disconnectCallback = callback;
    }
    setReconnectCallback(callback) {
        this.reconnectCallback = callback;
    }
    onAnnotationResults(message, source, responseCallback) {
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new Response());
        if (source === 0 /* LocalWorkflow */) {
            this.contextIdManager.applyContextIdOnOperations(message.ops);
        }
        // Notify annotation activation callbacks and apply operations if context candidates
        this.annotationResultsProcessor.process(message, (operation, item) => {
            this.applyOperationForContext(operation, item, source);
        }, (operation) => {
            this.callAnnotationCallbacks(operation, message.annotationType);
            if (this.hostCallbacks.onAnnotationResult) {
                try {
                    this.hostCallbacks.onAnnotationResult(operation);
                }
                catch (error) {
                    Log.error(0x2034575f /* tag_6nf35 */, LogCategory.CoreDefault, `onAnnotationResult error: ${error}`);
                }
            }
        }, (message) => {
            this.increaseWorkflowsItemsToProcess(message.ops);
            if (source !== 1 /* ServerWorkflow */) {
                const serverOps = message.ops.filter(op => isServerOp(op, this.serverInputTypes));
                this.submitOperationsToSession(serverOps, message.cv);
            }
            // TODO.Phase3 (6131360): Is checking workflowRuntime good enough here, or will the Phase3 SessionProxy
            // get AnnotationResultMessages from the server in addition to local workflows?
            if (this.workflowRuntime) {
                this.submitOperationsToSession(message.ops, message.cv);
            }
            else {
                this.localWorkflowManager.runLocalWorkflows(message.ops, this);
            }
        });
    }
    onAnnotationResultStateMessage(message, responseCallback) {
        var _a;
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new Response());
        for (const update of message.updates) {
            const { annotationType, state } = update;
            if (!this.tokensByAnnotationType.has(annotationType)) {
                continue;
            }
            // Get or determine old state if it's not available.
            // Updates are only sent if state has changed, so we can
            // determine the old state if it's not previously known.
            let oldState;
            if (this.annotationResultStates.has(annotationType)) {
                oldState = this.annotationResultStates.get(annotationType);
            }
            else {
                if (state === 0 /* Idle */) {
                    oldState = 1 /* Pending */;
                }
                else {
                    oldState = 0 /* Idle */;
                }
            }
            for (const token of this.tokensByAnnotationType.get(annotationType)) {
                const tracker = this.annotationActivationTrackers.get(token);
                if ((_a = tracker.options) === null || _a === void 0 ? void 0 : _a.stateUpdateCallback) {
                    tracker.options.stateUpdateCallback(oldState, state);
                }
            }
        }
    }
    submitOperationsToSession(sessionOps, cv) {
        const isSignalOp = (op) => SchemaObject.getTypeNameFor(op) === SignalOperation.getTypeName();
        const signalSessionOps = sessionOps.filter(op => isSignalOp(op));
        const modelSessionOps = sessionOps.filter(op => !isSignalOp(op));
        // Signal ops are sent immediately regardless of batch config and are not sequenced
        if (signalSessionOps.length > 0) {
            this.getSessionManager().sendMessage(new SyncMessage({
                cv: cv,
                ops: signalSessionOps
            }));
        }
        if (modelSessionOps.length > 0) {
            if (this.operationBatchConfig) {
                if (!this.batchedOperationsManager) {
                    const submit = (operations, batch, callback) => {
                        this.getSessionManager().sendMessage(new SyncMessage({
                            cv: batch.cv,
                            seq: this.nextSyncSequenceId++,
                            ops: operations
                        }), (errorResponse) => {
                            callback(errorResponse ? new Error(errorResponse.error) : undefined);
                        });
                    };
                    this.batchedOperationsManager = new BatchManager(submit);
                }
                this.batchedOperationsManager.addBatchItem(modelSessionOps, this.operationBatchConfig, { name: 'SubmitOperations' }, cv);
            }
            else {
                this.getSessionManager().sendMessage(new SyncMessage({
                    cv: cv,
                    seq: this.nextSyncSequenceId++,
                    ops: modelSessionOps
                }));
            }
        }
    }
    registerContextTypes(allContextTypes) {
        for (const contextType of allContextTypes) {
            // Register all context types of this workflow (both required and optional) and activate all their annotations on server side, so the server can return context annotations if they exist or get produced.
            this.activateAnnotation(contextType);
            this.registeredContextTypes.add(contextType);
        }
    }
    applyOperationForContext(operation, item, source) {
        let operationType = SchemaObject.getTypeNameFor(operation);
        if (operationType != AddOperation.getTypeName() && operationType != Operation.getTypeName() && operationType != UpdateOperation.getTypeName() && operationType != DeleteOperation.getTypeName()) {
            return;
        }
        // Assume that generic operation is always the same as Add.
        if (operationType == Operation.getTypeName()) {
            operationType = AddOperation.getTypeName();
        }
        // TODO #5326116: Currently, context annotation can be uploaded or returned from server on any path. This is fine for now
        // as local session is bound to a single user and a single document. We need proper session cache in local runtime to check
        // exact context holder paths.
        const itemWithPath = shallowCloneItemWithPath(this.resolvePlaceholdersInOperationParentPath(operation.parentPath), item);
        const parentPathKey = makeItemPathKey(itemWithPath.parentPath);
        if (operationType == AddOperation.getTypeName() || operationType == UpdateOperation.getTypeName()) {
            if (!itemWithPath.body) {
                return;
            }
            const contextType = SchemaObject.getTypeNameFor(itemWithPath.body);
            if (!this.registeredContextTypes.has(contextType)) {
                return;
            }
            let contextsOfType = this.availableContexts.get(contextType);
            if (!contextsOfType) {
                contextsOfType = new Map();
                this.availableContexts.set(contextType, contextsOfType);
            }
            let contextsForSource = contextsOfType.get(source);
            if (!contextsForSource) {
                contextsForSource = [];
                contextsOfType.set(source, contextsForSource);
            }
            if (!itemWithPath.id) {
                itemWithPath.id = this.localWorkflowManager.getNextClientAnnotationId();
            }
            if (operationType == AddOperation.getTypeName()) {
                if (contextsForSource.filter(context => makeItemPathKey(context.parentPath) == parentPathKey && context.id == itemWithPath.id).length == 0) {
                    contextsForSource.push(itemWithPath);
                }
                else {
                    Log.info(0x203cb30f /* tag_6plmp */, LogCategory.CoreDefault, `AddOperation for (${source}, ${itemWithPath.id}, ${itemWithPath.source}) can't be applied as the context item with matching path and ID already exists. Use UpdateOperation instead.`);
                }
            }
            else {
                for (let i = 0; i < contextsForSource.length; i++) {
                    if (contextsForSource[i].id == itemWithPath.id && makeItemPathKey(contextsForSource[i].parentPath) == parentPathKey) {
                        if (contextsForSource[i].source == itemWithPath.source) {
                            contextsForSource[i] = itemWithPath;
                            break;
                        }
                        else {
                            Log.warn(0x203cb310 /* tag_6plmq */, LogCategory.CoreDefault, `UpdateOperation for (${source}, ${itemWithPath.id}, ${itemWithPath.source}) can't be applied as the context item with provided path and ID has different source: ${contextsForSource[i].source}`);
                        }
                    }
                    else {
                        Log.warn(0x203cb311 /* tag_6plmr */, LogCategory.CoreDefault, `UpdateOperation for (${source}, ${itemWithPath.id}, ${itemWithPath.source}) can't be applied as the context item with matching path and ID was not found. AddOperation should be used instead`);
                    }
                }
            }
        }
        else if (operationType == DeleteOperation.getTypeName()) {
            if (!itemWithPath.id) {
                return;
            }
            this.availableContexts.forEach((contextsOfType, contextType) => {
                let contextsForSource = contextsOfType.get(source);
                if (!contextsForSource) {
                    return;
                }
                contextsForSource = contextsForSource.filter(context => !(makeItemPathKey(context.parentPath) == parentPathKey && context.id == itemWithPath.id));
                if (contextsForSource.length == 0) {
                    contextsOfType.delete(source);
                }
                else {
                    contextsOfType.set(source, contextsForSource);
                }
                if (contextsOfType.size == 0) {
                    this.availableContexts.delete(contextType);
                }
                else {
                    this.availableContexts.set(contextType, contextsOfType);
                }
            });
        }
    }
    increaseWorkflowsItemsToProcess(operations) {
        const workflows = this.localWorkflowManager.getAllRegisteredWorkflowsFromSession(this);
        for (const op of operations) {
            if (!supportedOperationSet.has(SchemaObject.getTypeNameFor(op))) {
                continue;
            }
            for (const item of op.items) {
                const inputType = SchemaObject.getTypeNameFor(item.body);
                for (const workflow of workflows || []) {
                    // We are only tracking inputs to Join Workflows
                    if (workflow.kind === WorkflowKind.Join && workflow.inputTypes.indexOf(inputType) !== -1) {
                        this.executionTrackersByWorkflowName.get(workflow.id).addInputItemToProcess(item.contextId);
                    }
                }
            }
        }
    }
    resolveRequestedContexts(workflow) {
        const requestedContexts = [];
        for (const [contextType, cardinality] of getContextTypes(workflow.requestedContextTypesRules)) {
            if (!this.availableContexts.has(contextType)) {
                if (cardinality == -3 /* Required */) {
                    return [false, []];
                }
                else {
                    continue;
                }
            }
            const allContextsOfType = Array.from(this.availableContexts.get(contextType).values()).reduce((acc, val) => acc.concat(val), []);
            if (allContextsOfType.length == 0) {
                throw new Error(`Assert: availableContexts have the entry for ${contextType} which does not contain any context annotation.`);
            }
            requestedContexts.push(...allContextsOfType);
        }
        return [true, requestedContexts];
    }
    getContextAnnotations(contextType, sourcePrefix, parentPath, sourceName) {
        var _a, _b;
        const parentPathKey = parentPath ? makeItemPathKey(parentPath) : undefined;
        return (_b = (_a = this.availableContexts.get(contextType)) === null || _a === void 0 ? void 0 : _a.get(sourcePrefix)) === null || _b === void 0 ? void 0 : _b.filter(context => (!parentPathKey || makeItemPathKey(context.parentPath) == parentPathKey) && (!sourceName || context.source == sourceName));
    }
    onWorkflowDefinitionOverrideMessage(message) {
        const logOp = new LogOperation({ operationName: 'WorkflowDefinitionOverride', success: true });
        const handleError = (message) => {
            logOp.resultDescription = message;
            logOp.success = false;
            Log.error(0x1f70969f /* tag_52j05 */, LogCategory.CoreDefault, logOp.stop());
            throw Error(`WorkflowDefinitionOverride: ${message}`);
        };
        const workflowDefinitionsByName = this.localWorkflowManager.getWorkflowDefinitionsByName(this);
        const sourceWorkflow = workflowDefinitionsByName.get(message.sourceWorkflowId);
        if (!sourceWorkflow) {
            handleError(`Workflow registration for source workflow '${message.targetWorkflowId}' was not found.`);
            return;
        }
        const targetWorkflow = workflowDefinitionsByName.get(message.targetWorkflowId);
        if (!targetWorkflow) {
            // Ignoring cases where the target workflow is not registered, we should not log an error here. This is specially usefull in testSuite that we can only register 1 workflow per test
            return;
        }
        if (!targetWorkflow.allowDefinitionOverride) {
            handleError(`Workflow '${targetWorkflow.id}' does not allow workflow definition override.`);
            return;
        }
        if (!sourceWorkflow.definitionOverrideTargetWorkflows || sourceWorkflow.definitionOverrideTargetWorkflows.indexOf(targetWorkflow.id) === -1) {
            handleError(`Workflow '${sourceWorkflow.id}' does not allow workflow definition for workflow '${targetWorkflow.id}'.`);
            return;
        }
        this.workflowDefinitionManager.mergeWorkflowDefinition(targetWorkflow, message.definition, message.contextId);
        logOp.success = true;
        logOp.resourceId = sourceWorkflow.id;
        logOp.resultDescription = `Updated config for workflow: ${message.targetWorkflowId}, context id: ${message.contextId}`;
        Log.info(0x1f7096a0 /* tag_52j06 */, LogCategory.CoreDefault, logOp.stop());
    }
    applyContextIdOnOperations(ops) {
        this.contextIdManager.applyContextIdOnOperations(ops);
    }
    getExecutionTrackers() {
        return this.executionTrackersByWorkflowName;
    }
    enableDeltaGenerator() {
        var _a;
        this.deltaGenerator = (_a = this.deltaGenerator) !== null && _a !== void 0 ? _a : new DeltaGenerator();
    }
    createDeltasFromUpdateOperation(op) {
        var _a, _b;
        const retVal = [];
        Log.info(0x1f38318c /* tag_5odgm */, LogCategory.CoreDefault, `Calling delta create for UpdateOperation under parent path [${op.parentPath}] (${(_a = op.items) === null || _a === void 0 ? void 0 : _a.length} item(s), first item id: ${((_b = op.items) === null || _b === void 0 ? void 0 : _b.length) > 0 ? op.items[0].id : '(no items)'})`);
        let currentPath = [...op.parentPath];
        for (const item of op.items) {
            const updatedItems = [];
            const deltas = this.deltaGenerator.createTextTileDeltasFromItem(item, op.parentPath);
            if (!(deltas === null || deltas === void 0 ? void 0 : deltas.length)) {
                Log.info(0x1f316060 /* tag_5mwb6 */, LogCategory.CoreDefault, `Failed to create delta on item ${item.id}`);
                continue;
            }
            for (const delta of deltas) {
                const id = Utils.uuidv4();
                currentPath = [...op.parentPath, item.id];
                const updatedItem = {
                    id,
                    revId: item.revId,
                    body: delta,
                    contextId: item.contextId,
                    source: item.source
                };
                updatedItems.push(updatedItem);
            }
            if (updatedItems.length > 0) {
                // create a delta update operation and apply it to the model
                retVal.push(new DeltaUpdateOperation({ parentPath: currentPath, items: updatedItems }));
            }
        }
        return retVal;
    }
    addToWorkflowGraph(workflow) {
        this.workflowGraph.addWorkflow(getWorkflowDefinitionBase(workflow));
    }
    getAuthToken(tokenType, cb) {
        const tokenRequest = { Tickets: [] };
        if (this.clientMetadata.docSessionId) {
            tokenRequest.DocSessionId = this.clientMetadata.docSessionId;
        }
        const clientTokenType = getClientAuthTokenType(tokenType);
        if (clientTokenType) {
            tokenRequest.TokenType = clientTokenType;
        }
        this.hostCallbacks.requestAuthToken(tokenRequest)
            .then((tokenResponse) => {
            var _a;
            if (!tokenResponse) {
                cb(new Error('Missing AuthTokenResponse from requestAuthToken'));
                return;
            }
            cb(undefined, tokenResponse.Token, {
                returnedTokenType: tokenType,
                timeToLiveSec: (_a = tokenResponse.TokenProperties) === null || _a === void 0 ? void 0 : _a.timeToLiveSec
            });
            return;
        })
            .catch((error) => {
            return cb(error);
        });
    }
    resetNextSyncSequenceId() {
        this.nextSyncSequenceId = 1;
    }
    onReconnect() {
        this.isSessionConnected = true;
        this.annotationActivationTrackers.forEach((tracker) => {
            tracker.activate(true /*ignoreExistingAnnotations*/, true /*sendOnlyIfConnected*/);
        });
        if (this.reconnectCallback) {
            this.reconnectCallback();
        }
    }
    onSessionClose(sessionCloseMessage) {
        this.isSessionClosed = true;
        this.isSessionConnected = false;
        if (this.sessionCloseCallback) {
            this.sessionCloseCallback(sessionCloseMessage);
        }
    }
    onConnect(isSeedingRequired, sessionUrl, origin, authToken, serverInputTypes) {
        if (this.hasSessionConnected) {
            // If seeding is required on reconnect reset allowSeed flags, seedGroupSize counter, and seedBatchedOperationsManager.
            // Otherwise we will continue with already started seeding if reconnect occurred during the seeding
            if (isSeedingRequired) {
                this.allowSeed = true;
                this.allowGroupSeed = true;
                this.seedGroupSize = 0;
                if (this.seedBatchedOperationsManager) {
                    this.seedBatchedOperationsManager.removeAllBatchedItems();
                    this.seedBatchedOperationsManager = undefined;
                }
                if (this.batchedOperationsManager) {
                    this.batchedOperationsManager.removeAllBatchedItems();
                    this.batchedOperationsManager = undefined;
                }
            }
        }
        else {
            this.sendWorkflowGraphInitMessage();
        }
        this.isSessionConnected = true;
        this.hasSessionConnected = true;
        if (this.connectCallback) {
            this.connectCallback(isSeedingRequired, sessionUrl, origin, authToken);
        }
        this.serverInputTypes = serverInputTypes;
    }
    onDisconnect(error) {
        this.isSessionConnected = false;
        if (this.disconnectCallback) {
            this.disconnectCallback(error);
        }
    }
    getSessionManager() {
        if (!this.sessionManager) {
            this.sessionManager = new EmptySessionManager();
        }
        return this.sessionManager;
    }
    callAnnotationCallbacks(op, annotationType) {
        if (this.annotationCallbacks.size === 0) {
            return;
        }
        const callbackMap = this.annotationCallbacks.get(annotationType);
        if (callbackMap) {
            callbackMap.forEach((callback) => {
                callback(op);
            });
        }
    }
    getOperationBatchConfig(batchOptions) {
        // Group all arrays of operations together
        const groupingKeyExtractor = () => {
            return 'operations';
        };
        // Combine operations into a single array
        const multiplex = (operations) => {
            return {
                input: operations.filter(ops => ops.length).reduce((ops, array) => ops.concat(array), []),
                demultiplex: () => [[]] // NOTE: Unused because no callback is specified to addBatchItem
            };
        };
        // Split large operations arrays into smaller arrays
        const split = (operations) => {
            // Cannot split array with 1 or fewer operations
            if (operations.length <= 1) {
                return undefined;
            }
            return {
                inputs: operations.reduce((ops, op) => {
                    ops.push([op]);
                    return ops;
                }, []),
                join: () => [] // NOTE: Unused because no callback is specified to addBatchItem
            };
        };
        return {
            delayMs: batchOptions.delayMs,
            maxInputSize: batchOptions.maxInputSize,
            estimateSize: estimateSize,
            groupingKeyExtractor: groupingKeyExtractor,
            multiplex: multiplex,
            split: split
        };
    }
    sendSeedMessagesViaBatchManager(operations, groupComplete, cv) {
        if (!this.seedBatchedOperationsManager) {
            // Submit will be invoked once per batch of operations
            const submit = (operations, batch, callback) => {
                // Update the count of batches
                this.seedGroupSize++;
                // Send the SyncMessage for the operations in this batch
                this.getSessionManager().sendMessage(new SyncMessage({
                    cv: batch.cv,
                    seq: 0,
                    ops: operations,
                    groupId: 'Seed',
                    groupSize: batch.groupComplete ? this.seedGroupSize : undefined,
                    groupComplete: batch.groupComplete ? batch.groupComplete : undefined
                }), (errorResponse) => {
                    callback(errorResponse ? new Error(errorResponse.error) : undefined);
                });
            };
            // Instantiate the seed batch manager
            this.seedBatchedOperationsManager = new BatchManager(submit);
        }
        // Instantiate the seed batch manager and add the seed operations to it
        this.seedBatchedOperationsManager.addBatchItem(operations, this.operationBatchConfig, { name: 'SubmitSeedOperations' }, cv, groupComplete);
    }
    onAnnotationResultsFromServer(message, responseCallback) {
        this.onAnnotationResults(message, 1 /* ServerWorkflow */, responseCallback);
    }
    getLocalRegisteredWorkflows() {
        return this.localWorkflowManager.getAllRegisteredWorkflowsFromSession(this).map(workflow => getWorkflowDefinitionBase(workflow));
    }
    sendWorkflowGraphInitMessage() {
        const localWorkflows = this.getLocalRegisteredWorkflows();
        const logOp = new LogOperation({ operationName: 'WorkflowGraphInit', success: true, resultDescription: `sending WorkflowGraphInit message ${localWorkflows.map(w => w.id)}` }).start();
        // TOOD 6169156: Only send relevant local workflows to downstream instance, like Exclusive workflows
        const workflowGraphInitMessage = new WorkflowGraphInitMessage({ upstreamRuntimeWorkflows: localWorkflows });
        this.getSessionManager().sendMessage(workflowGraphInitMessage, (error, response) => {
            if (error) {
                logOp.success = false;
                logOp.resultDescription = `${logOp.resultDescription}. error: ${error}}`;
                Log.error(0x1f403154 /* tag_5qdfu */, LogCategory.CoreDefault, logOp.stop());
                return;
            }
            Log.info(0x1f403155 /* tag_5qdfv */, LogCategory.CoreDefault, logOp.stop());
            this.onWorkflowGraphInitResponse(response);
        });
    }
    trySendWorkflowGraphInitMessage() {
        if (!this.graphInitMessageTimer && !this.skipGraphInitOnWorkflowRegistration) {
            this.graphInitMessageTimer = true;
            setTimeout(() => {
                this.graphInitMessageTimer = false;
                this.sendWorkflowGraphInitMessage();
            }, graphInitDelayMs);
        }
    }
    onWorkflowGraphInitResponse(response) {
        // We are adding the downstream runtime workflows to the workflow graph.
        // It is worth mentioning that we cannot add two workflows with the same workflowId and we make this check inside the addWorkflow method.
        // So if there is a local workflow with the same workflow id from any of the external workflows, it will not be added to the graph.
        // To prevent versioning mismatch downstream workflows, we are removing all of them from the graph and adding the most updated back.
        this.workflowGraph.removeWorkflows(true /** external */);
        for (const workflow of response.downstreamRuntimeWorkflows || []) {
            this.workflowGraph.addWorkflow(workflow, true /**external workflow*/);
        }
    }
    resolvePlaceholdersInOperationParentPath(parentPath) {
        // AL server replaces #userContext# and #tenantContext# placeholders with actual paths of context holders for the current user and tenant. However, local session is not aware of userID and tenantID as it's bound to a single user.
        // So, if local runtime received context with already resolved user / tenant context holder path, replace it back with placeholder, so we don't need to track user / tenant contexts under the two parents.
        let localParentPath = parentPath;
        if (localParentPath.length == 3 && localParentPath[0] == 'session' && localParentPath[1] == 'user' && localParentPath[2].startsWith('user_')) {
            Log.info(0x203cb312 /* tag_6plms */, LogCategory.CoreDefault, `Replacing user context placeholder for: ${makeItemPathKey(parentPath)}.`);
            localParentPath = ['session', '#userContext#'];
        }
        if (localParentPath.length == 3 && localParentPath[0] == 'session' && localParentPath[1] == 'user' && localParentPath[2].startsWith('tenant_')) {
            Log.info(0x203cb313 /* tag_6plmt */, LogCategory.CoreDefault, `Replacing tenant context placeholder for: ${makeItemPathKey(parentPath)}.`);
            localParentPath = ['session', '#tenantContext#'];
        }
        return localParentPath;
    }
}
//# sourceMappingURL=session-proxy.js.map